import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  MEMBER_AUTH_UNAUTHORIZED_EVENT,
  changePassword,
  clearStoredAuthSession,
  getStoredMemberSession,
  loginMember,
  registerMember,
  setStoredAuthSession,
  validateMemberSession,
} from "../shared/api";

const ClientContext = createContext();
const TEN_MINUTES_IN_MS = 10 * 60 * 1000;
const AUTH_ME_REFRESH_INTERVAL_MS = TEN_MINUTES_IN_MS;

export const useClient = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error("useClient must be used within a ClientProvider");
  }
  return context;
};

const buildClientInfoFromSession = (session) => {
  if (!session?.token) return null;

  const permissions = Array.isArray(session.permissions)
    ? session.permissions
    : [];

  return {
    token: session.token,
    member: session.member || null,
    account: session.account || null,
    clientId: session.clientId || null,
    permissions,
  };
};

export const ClientProvider = ({ children }) => {
  const [clientInfo, setClientInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sessionWarningKey, setSessionWarningKey] = useState("");

  const refreshSession = useCallback(
    async ({ keepPreviousOnPermissionFailure = true } = {}) => {
      const storedSession = getStoredMemberSession();
      if (!storedSession?.token) {
        setClientInfo(null);
        return { success: false };
      }

      try {
        const authMeData = await validateMemberSession();
        const refreshedSession = setStoredAuthSession({
          token: storedSession.token,
          ...authMeData,
        });

        setClientInfo(buildClientInfoFromSession(refreshedSession));
        setSessionWarningKey("");
        return { success: true };
      } catch (error) {
        if (
          keepPreviousOnPermissionFailure &&
          error?.status === 503 &&
          error?.error_code === "permissions_unavailable"
        ) {
          setClientInfo(
            (prev) => prev || buildClientInfoFromSession(storedSession),
          );
          setSessionWarningKey("auth.errors.permissionsUnavailable");
          return { success: false, isTemporaryFailure: true, error };
        }

        clearStoredAuthSession();
        setClientInfo(null);
        setSessionWarningKey("");
        return { success: false, error };
      }
    },
    [],
  );

  useEffect(() => {
    const syncSessionFromStorage = async () => {
      const storedSession = getStoredMemberSession();
      if (!storedSession?.token) {
        setClientInfo(null);
        setIsLoading(false);
        return;
      }

      const result = await refreshSession({
        keepPreviousOnPermissionFailure: false,
      });

      if (!result.success && !result.isTemporaryFailure) {
        setClientInfo(null);
      }
      setIsLoading(false);
    };

    syncSessionFromStorage();
  }, [refreshSession]);

  useEffect(() => {
    if (!clientInfo?.token) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      refreshSession();
    }, AUTH_ME_REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [clientInfo?.token, refreshSession]);

  useEffect(() => {
    const handleUnauthorized = () => {
      clearStoredAuthSession();
      setClientInfo(null);
      setSessionWarningKey("");
    };

    window.addEventListener(MEMBER_AUTH_UNAUTHORIZED_EVENT, handleUnauthorized);
    return () => {
      window.removeEventListener(
        MEMBER_AUTH_UNAUTHORIZED_EVENT,
        handleUnauthorized,
      );
    };
  }, []);

  const signIn = async ({ email, password }) => {
    try {
      const data = await loginMember({ email, password });
      const session = setStoredAuthSession(data);
      setClientInfo(buildClientInfoFromSession(session));
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    }
  };

  const createMember = async ({ email, password, privateKey, department }) => {
    try {
      const data = await registerMember({
        email,
        password,
        privateKey,
        department,
      });
      const session = setStoredAuthSession(data);
      setClientInfo(buildClientInfoFromSession(session));
      return { success: true, data };
    } catch (error) {
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  const hasPermission = (permission) => {
    if (!permission) return true;
    return clientInfo?.permissions?.includes(permission) || false;
  };

  const updatePassword = async ({ currentPassword, newPassword }) => {
    try {
      await changePassword({ currentPassword, newPassword });
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  };

  const logout = () => {
    clearStoredAuthSession();
    setClientInfo(null);
    setSessionWarningKey("");
  };

  const value = {
    clientInfo,
    isLoading,
    signIn,
    createMember,
    updatePassword,
    logout,
    hasPermission,
    refreshSession,
    sessionWarningKey,
    isAuthenticated: !!clientInfo,
  };

  return (
    <ClientContext.Provider value={value}>{children}</ClientContext.Provider>
  );
};
